"use client";

/**
 * Animated SVG illustration for the /trends flow tile on the landing page.
 *
 * The line DRAWS ITSELF (stroke-dashoffset over the measured path length)
 * with a soft under-glow (a second, wider, low-opacity stroke of the same
 * path running the same dash animation) and a TRAVELING TIP that rides the
 * vertices in lockstep. Sync trick: both the dash draw and the tip's
 * per-segment translate() keyframes are paced LINEARLY over the same window,
 * and the tip's keyframe percentages are placed at each vertex's cumulative
 * length fraction, so dash distance and tip distance always agree.
 *
 * The area fill is clip-revealed in lockstep with the line draw (the
 * chart plots together with its area), then the tip lands with a pulse
 * and a "+6%" uptrend pill rises in. The whole scene holds, fades, and
 * pauses briefly before the next cycle - a calm 7s loop.
 *
 * Conventions match the sibling flow animations: 200x160 viewBox, design
 * tokens via var(...), transform/opacity/stroke-dashoffset-only motion,
 * memo() wrapped, and a reduced-motion fallback that lands on the final
 * frame (line fully drawn, tip parked, pill visible).
 */
import { memo } from "react";
import { useScenePause } from "./useScenePause";

function TrendsAnimation() {
  const sceneRef = useScenePause<SVGSVGElement>();
  // Plot area; y is inverted (SVG), so a rising market = decreasing y.
  // Vertices: (24,116) (56,122) (88,86) (120,96) (152,58) (184,40).
  const LINE = "M24 116 L56 122 L88 86 L120 96 L152 58 L184 40";
  const AREA = `${LINE} L184 132 L24 132 Z`;
  // Measured polyline length (sum of segments ≈ 200.7); slightly padded so
  // the dash fully hides the line at offset = LEN.
  const LEN = 202;

  const C_RULE = "var(--color-rule)";
  const C_ACCENT = "var(--color-accent)";
  const C_POSITIVE = "var(--color-positive)";

  return (
    <svg ref={sceneRef} viewBox="0 0 200 160" className="scene-svg w-full h-44" aria-hidden="true">
      <defs>
        <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C_ACCENT} stopOpacity="0.22" />
          <stop offset="1" stopColor={C_ACCENT} stopOpacity="0" />
        </linearGradient>
        <clipPath id="trendReveal">
          <rect className="tr-reveal" x="24" y="0" width="160" height="160" />
        </clipPath>
      </defs>

      {/* faint baseline grid (always visible, anchors the scene mid-cycle) */}
      {[52, 82, 112].map((y) => (
        <line key={y} x1="24" y1={y} x2="184" y2={y} stroke={C_RULE} strokeWidth="1" />
      ))}

      {/* area fill - revealed left-to-right in lockstep with the line
          draw (same linear window), so the chart plots WITH its area. */}
      <g clipPath="url(#trendReveal)">
        <path className="tr-area" d={AREA} fill="url(#trendArea)" />
      </g>

      {/* soft under-glow: same path, wide low-opacity stroke, same draw */}
      <path
        className="tr-glow"
        d={LINE}
        fill="none"
        stroke={C_ACCENT}
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={LEN}
        strokeDashoffset={LEN}
      />

      {/* the line itself - draws via stroke-dashoffset */}
      <path
        className="tr-line"
        d={LINE}
        fill="none"
        stroke={C_ACCENT}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={LEN}
        strokeDashoffset={LEN}
      />

      {/* traveling tip - rides the vertices in sync with the draw, then
          lands on the latest point with a pulse. The halo circle gives it
          a soft glow without any filter. */}
      <g className="tr-tip">
        <circle cx="24" cy="116" r="7" fill={C_ACCENT} fillOpacity="0.18" />
        <circle cx="24" cy="116" r="3.2" fill="var(--color-paper)" stroke={C_ACCENT} strokeWidth="2.2" />
      </g>

      {/* uptrend pill - rises in as the line completes */}
      <g className="tr-pill">
        <rect x="116" y="14" width="58" height="20" rx="10" fill={C_POSITIVE} fillOpacity="0.16" />
        <path d="M126 28 l4 -4 l3 2 l5 -6" fill="none" stroke={C_POSITIVE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <text x="144" y="27.5" fill={C_POSITIVE} fontSize="10" fontWeight="700">+6%</text>
      </g>

      <style>{`
        /* 7s loop:
             0-4%    blank beat, line fades in at the first point
             4-40%   the line draws itself (LINEAR pacing = tip sync),
                     area + context line fade in underneath
             40-48%  tip lands + pulses, pill rises in
             48-90%  hold the finished chart
             90-96%  everything fades
             96-100% quiet pause before the next cycle */
        @keyframes trLine {
          0%        { stroke-dashoffset: ${LEN}; opacity: 0; }
          4%        { stroke-dashoffset: ${LEN}; opacity: 1; }
          40%       { stroke-dashoffset: 0; }
          90%       { stroke-dashoffset: 0; opacity: 1; }
          96%, 100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes trGlow {
          0%        { stroke-dashoffset: ${LEN}; opacity: 0; }
          4%        { stroke-dashoffset: ${LEN}; opacity: 0.16; }
          40%       { stroke-dashoffset: 0; }
          90%       { stroke-dashoffset: 0; opacity: 0.16; }
          96%, 100% { stroke-dashoffset: 0; opacity: 0; }
        }
        /* Tip keyframe stops sit at each vertex's cumulative-length fraction
           of the 4-40% draw window, so it tracks the dash head exactly:
           32.56 / 80.73 / 114.26 / 163.94 / 200.65 of ~200.65 total. */
        @keyframes trTip {
          0%        { opacity: 0; transform: translate(0px, 0px); }
          4%        { opacity: 1; transform: translate(0px, 0px); }
          9.8%      { transform: translate(32px, 6px); }
          18.5%     { transform: translate(64px, -30px); }
          24.5%     { transform: translate(96px, -20px); }
          33.4%     { transform: translate(128px, -58px); }
          40%       { transform: translate(160px, -76px) scale(1); }
          44%       { transform: translate(160px, -76px) scale(1.35); }
          48%       { transform: translate(160px, -76px) scale(1); }
          90%       { opacity: 1; transform: translate(160px, -76px) scale(1); }
          96%, 100% { opacity: 0; transform: translate(160px, -76px) scale(1); }
        }
        @keyframes trArea {
          0%        { opacity: 0; }
          4%        { opacity: 1; }
          90%       { opacity: 1; }
          96%, 100% { opacity: 0; }
        }
        @keyframes trReveal {
          0%, 4%    { transform: scaleX(0); }
          40%       { transform: scaleX(1); }
          100%      { transform: scaleX(1); }
        }
        @keyframes trPill {
          0%, 40%   { opacity: 0; transform: translateY(5px); }
          48%       { opacity: 1; transform: translateY(0); }
          90%       { opacity: 1; transform: translateY(0); }
          96%, 100% { opacity: 0; transform: translateY(0); }
        }
        /* Linear pacing is REQUIRED on line/glow/tip - it is what keeps the
           dash head and the traveling tip in sync. */
        .tr-line { animation: trLine 7s linear infinite; }
        .tr-glow { animation: trGlow 7s linear infinite; }
        .tr-tip  { transform-box: fill-box; transform-origin: center; animation: trTip 7s linear infinite; }
        .tr-area { animation: trArea 7s linear infinite; }
        .tr-reveal {
          transform-origin: 24px 0px;
          animation: trReveal 7s linear infinite;
        }
        .tr-pill { transform-box: fill-box; transform-origin: center; animation: trPill 7s cubic-bezier(.22,1,.36,1) infinite; }
        @media (prefers-reduced-motion: reduce) {
          .tr-line, .tr-glow, .tr-tip, .tr-area, .tr-reveal, .tr-pill { animation: none; }
          .tr-line { stroke-dashoffset: 0; opacity: 1; }
          .tr-glow { stroke-dashoffset: 0; opacity: 0.16; }
          .tr-tip  { opacity: 1; transform: translate(160px, -76px); }
          .tr-area, .tr-pill { opacity: 1; transform: none; }
          .tr-reveal { transform: scaleX(1); }
        }
      `}</style>
    </svg>
  );
}

export default memo(TrendsAnimation);
